import { CdkScrollable } from "@angular/cdk/scrolling";
import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, FormGroup, ReactiveFormsModule, ValidationErrors } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { ToastService } from "@tylertech/core-module";
import { IShellService } from "@tylertech/incode-module";
import { CustomValidatorMessage, LgdCustomAsyncValidator, LgdFormControlFactory, LgdPatternValidator, LgdTextComponent, LgdTypeAheadComponent } from "@tylertech/lgd-reactive-forms";
import { IdValuePair } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ServiceAddressViewModel, SiteAddressViewModel } from "../../../../interfaces";
import { ReadGroupService, ServiceAddressService } from "../../../../services";

export interface IdValuePairWithParentId extends IdValuePair
{
    ParentId: string;
}

export interface ServiceAddressWizardDialogData
{
    readonly serviceAddressNumberMask: string;
}

@Component({
    selector: "service-address-wizard-dialog",
    templateUrl: "./serviceAddressWizardDialog.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        CdkScrollable,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        LgdTextComponent,
        LgdTypeAheadComponent,
        MatButton,
        MatDialogActions,
        MatDialogClose,
        MatDialogContent,
        MatDialogTitle,
        MatIcon,
        ReactiveFormsModule,
    ]
})
export class ServiceAddressWizardDialogComponent implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<ServiceAddressWizardDialogComponent, IdValuePairWithParentId | undefined>>(MatDialogRef);
    private readonly data = inject<ServiceAddressWizardDialogData>(MAT_DIALOG_DATA);
    private readonly toastService = inject(ToastService);
    private readonly $shellService = inject(IShellService);
    private readonly $serviceAddressService = inject(ServiceAddressService);
    private readonly $readGroupService = inject(ReadGroupService);

    private readonly readGroupsSubject = new BehaviorSubject<Array<IdValuePair>>([]);
    readonly formGroup: FormGroup;
    private forceShowRequiredError: boolean = false;

    private siteAddress: SiteAddressViewModel;
    isProcessing: boolean;

    constructor()
    {
        this.formGroup = new FormGroup(
            {
                // TODO - this should be a typeahead, we shouldn't have to launch a shell dialog to select a value....
                siteAddress: LgdFormControlFactory.text({
                    disabled: true,
                    value: undefined
                }),
                serviceAddressNumber: LgdFormControlFactory.text(
                    "",
                    true,
                    {},
                    [
                        new AddressPatternValidator(this.serviceAddressNumberMask)
                    ],
                    [
                        new ServiceAddressUniqueAsyncValidator(this.$serviceAddressService)
                    ]),
                readGroup: LgdFormControlFactory.typeahead<IdValuePair>(
                    undefined,
                    true,
                    {
                        displayValueFn: x => x?.Value || ""
                    }),
            });
    }

    private readonly destructor$ = new Subject<void>();
    private get serviceAddressNumberMask(): string { return this.data.serviceAddressNumberMask; }

    private get siteAddressControl(): AbstractControl<string> { return this.formGroup.get("siteAddress"); }
    private get serviceAddressNumberControl(): AbstractControl<string> { return this.formGroup.get("serviceAddressNumber"); }
    private get readGroupControl(): AbstractControl<IdValuePair> { return this.formGroup.get("readGroup"); }

    get readGroupsObservable(): Observable<Array<IdValuePair>> { return this.readGroupsSubject.asObservable(); }
    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (this.siteAddress)
        {
            this.siteAddressControl.setValue(this.siteAddress.Location);
        }
        else
        {
            this.siteAddressControl.setValue("");
        }

        const self = this;
        subscribeToValueChanges(this.readGroupControl, (x: IdValuePair) =>
        {
            if (x)
            {
                this.getNextServiceAddressNumber(x.Id);
            }
        });
        function subscribeToValueChanges<T>(control: AbstractControl, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(subscriptionFn);
        }
    }

    lookupReadGroups($event: string): void
    {
        this.$readGroupService.lookup($event)
            .then((results: Array<IdValuePair>) =>
            {
                this.readGroupsSubject.next(results);
            });
    }

    // TODO - siteAddressControl should be a typeahead, we shouldn't have to launch a shell dialog to select a value....
    lookupSiteAddress(): void
    {
        this.isProcessing = true;
        this.$shellService.executeShellFunction<SiteAddressViewModel>("LaunchSiteAddressWizard", null)
            .then(result =>
            {
                if (result.Success)
                {
                    this.siteAddress = result.Result;
                    this.forceShowRequiredError = false;

                    this.siteAddressControl.setValue(this.siteAddress.Location);
                    this.siteAddressControl.updateValueAndValidity();
                }
                else
                {
                    this.toastService.showToast(
                        JSON.stringify(result.Result),
                        0);
                }
            })
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }

    getNextServiceAddressNumber(readGroupId: string): void
    {
        this.$serviceAddressService.getNextServiceAddressNumber(readGroupId)
            .then(
                (result: string) =>
                {
                    this.serviceAddressNumberControl.setValue(result);
                    this.serviceAddressNumberControl.updateValueAndValidity();
                }
            );
    }

    showRequiredError(): boolean
    {
        return this.forceShowRequiredError || (this.formGroup.touched && this.formGroup.hasError("required"));
    }

    submit(): void
    {
        if (this.formGroup.invalid)
        {
            return;
        }

        if (!this.siteAddress)
        {
            this.forceShowRequiredError = true;
            return;
        }

        const interfaceSA: ServiceAddressViewModel = {
            SiteAddressId: this.siteAddress.Id,
            ServiceAddressNumber: this.serviceAddressNumberControl.value,
            ReadGroupCodeId: this.readGroupControl.value.Id,
        };

        this.isProcessing = true;
        this.$serviceAddressService.createServiceAddress(interfaceSA)
            .then(
                (result: string) =>
                {
                    const saIVP: IdValuePairWithParentId = {
                        ParentId: this.serviceAddressNumberControl.value,
                        Id: result,
                        Value: this.siteAddress.Location,
                    };

                    this.dialogRef.close(saIVP);
                },
                () =>
                {
                    this.isProcessing = false;
                    const textContent = `There was an error in saving to the Database. Service Address ${this.serviceAddressNumberControl.value} was not successfully created. If the issue persists, please contact Tyler Support.`;
                    this.toastService.showToast(textContent, 0);
                }
            );
    }
}

class AddressPatternValidator extends LgdPatternValidator
{
    constructor(pattern: string)
    {
        super(new RegExp(pattern));
    }
}

class ServiceAddressUniqueAsyncValidator extends LgdCustomAsyncValidator
{
    constructor(
        private readonly serviceAddressService: ServiceAddressService)
    {
        super();
    }

    getValidatorFn(): AsyncValidatorFn
    {
        const self = this;
        return validatorFn;

        function validatorFn(control: AbstractControl): Promise<ValidationErrors | null>
        {
            const currentValue = control.value;
            return currentValue
                ? self.serviceAddressService.checkUnique(currentValue)
                    .then((isUnique: boolean) =>
                    {
                        return !isUnique
                            ? { unique: { value: currentValue } }
                            : null;
                    })
                : Promise.resolve(null);
        }
    }

    getErrorMessage(label: string): readonly CustomValidatorMessage[]
    {
        return [
            {
                errorKey: "unique",
                message: `${label} must be unique.`
            }
        ];
    }
}

